import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { Plus, Calendar, Clock, Trash2, Loader2, Save, X, Phone, Mail, Video, Copy, Check, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function Agenda() {
    const { user } = useAuth();
    const isResponsable = user?.role === "responsable";
    const isAdmin = user?.role === "admin";
    const canView = isResponsable || isAdmin;
    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [copied, setCopied] = useState(false);

    const reload = async () => {
        try {
            const { data } = await api.get("/agenda/slots");
            setSlots(data);
        } catch (e) { /* ignore */ }
        finally { setLoading(false); }
    };
    useEffect(() => { reload(); }, []);

    const removeSlot = async (id) => {
        if (!window.confirm("Supprimer ce créneau ?")) return;
        try {
            await api.delete(`/agenda/slots/${id}`);
            toast.success("Créneau supprimé");
            reload();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        }
    };

    const publicLink = user?.invite_code ? `${window.location.origin}/rdv/${user.invite_code}` : "";
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(publicLink);
            setCopied(true);
            toast.success("Lien copié !");
            setTimeout(() => setCopied(false), 2200);
        } catch { toast.error("Impossible de copier"); }
    };

    const futureSlots = slots.filter((s) => !s.booking);
    const bookedSlots = slots.filter((s) => s.booking);

    return (
        <div className="min-h-screen bg-paper">
            <Navbar />
            <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="agenda-page">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
                    <div>
                        <div className="label-eyebrow mb-2">Agenda & rendez-vous tuteurs</div>
                        <h1 className="h-display text-4xl sm:text-5xl font-semibold leading-tight">
                            Vos <span className="text-orange">créneaux</span> de rencontre
                        </h1>
                        <p className="mt-3 text-sm text-muted2 max-w-xl">
                            Proposez des créneaux. Les tuteurs en entreprise réservent directement via votre lien public.
                        </p>
                    </div>
                    {isResponsable && (
                        <button onClick={() => setShowForm(true)} className="btn-primary !py-3 self-start sm:self-auto" data-testid="agenda-new-slot-button">
                            <Plus size={16} /> Nouveau créneau
                        </button>
                    )}
                </div>

                {isResponsable && publicLink && (
                    <div className="panel p-5 mb-8 bg-ink text-white border-ink" data-testid="agenda-public-link-card">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                                <div className="label-eyebrow text-orange/80 mb-1">Lien public pour les tuteurs</div>
                                <p className="text-xs text-white/70">Partagez ce lien à vos tuteurs : ils choisissent un créneau et réservent — aucun compte requis.</p>
                            </div>
                            <div className="flex items-center gap-2 flex-1 max-w-md">
                                <input readOnly value={publicLink} className="field-input flex-1 font-mono text-xs" onFocus={(e) => e.target.select()} data-testid="agenda-public-link-input" />
                                <button onClick={copyLink} className="btn-outline !bg-transparent !text-white !border-white/30 hover:!bg-white/10" data-testid="agenda-public-link-copy">
                                    {copied ? <Check size={14} /> : <Copy size={14} />}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showForm && isResponsable && (
                    <SlotForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); reload(); }} />
                )}

                {loading ? (
                    <div className="panel p-10 text-center text-muted2 text-sm">Chargement…</div>
                ) : (
                    <>
                        <h2 className="h-display text-xl font-semibold mb-3">Créneaux disponibles ({futureSlots.length})</h2>
                        {futureSlots.length === 0 ? (
                            <div className="panel p-8 text-center text-sm text-muted2 mb-8" data-testid="agenda-empty-available">Aucun créneau disponible.</div>
                        ) : (
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-10">
                                {futureSlots.map((s) => (
                                    <div key={s.id} className="panel p-4 group relative" data-testid={`slot-card-${s.id}`}>
                                        <div className="flex justify-between items-start gap-2">
                                            <span className="pill bg-emerald-50 text-emerald-700 border border-emerald-200">Disponible</span>
                                            {isResponsable && (
                                                <button onClick={() => removeSlot(s.id)} className="text-muted2 hover:text-destructive p-1"><Trash2 size={14} /></button>
                                            )}
                                        </div>
                                        <h3 className="h-display text-base font-semibold mt-2">{s.title}</h3>
                                        <div className="text-xs font-mono text-muted2 mt-2 flex items-center gap-1.5"><Calendar size={12} /> {s.date}</div>
                                        <div className="text-xs font-mono text-muted2 mt-1 flex items-center gap-1.5"><Clock size={12} /> {s.start_time} → {s.end_time}</div>
                                    </div>
                                ))}
                            </div>
                        )}

                        <h2 className="h-display text-xl font-semibold mb-3">Rendez-vous réservés ({bookedSlots.length})</h2>
                        {bookedSlots.length === 0 ? (
                            <div className="panel p-8 text-center text-sm text-muted2" data-testid="agenda-empty-booked">Aucune réservation pour le moment.</div>
                        ) : (
                            <div className="space-y-2">
                                {bookedSlots.map((s) => (
                                    <div key={s.id} className="panel p-4 border-l-4 border-l-orange" data-testid={`booking-card-${s.id}`}>
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                            <div>
                                                <div className="text-xs font-mono text-muted2 flex items-center gap-3 mb-1.5">
                                                    <span className="flex items-center gap-1"><Calendar size={12} /> {s.date}</span>
                                                    <span className="flex items-center gap-1"><Clock size={12} /> {s.start_time} → {s.end_time}</span>
                                                </div>
                                                <div className="font-semibold">{s.booking.tutor_prenom} {s.booking.tutor_nom}</div>
                                                {s.booking.company && <div className="text-xs text-muted2">{s.booking.company}</div>}
                                                <div className="mt-2 text-xs flex flex-wrap gap-3">
                                                    <a href={`mailto:${s.booking.tutor_email}`} className="inline-flex items-center gap-1 hover:text-orange"><Mail size={11} /> {s.booking.tutor_email}</a>
                                                    {s.booking.tutor_phone && <a href={`tel:${s.booking.tutor_phone}`} className="inline-flex items-center gap-1 hover:text-orange"><Phone size={11} /> {s.booking.tutor_phone}</a>}
                                                    {s.booking.visio_link && <a href={s.booking.visio_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-orange hover:underline"><Video size={11} /> Lien visio <ExternalLink size={10} /></a>}
                                                </div>
                                                {s.booking.message && <p className="text-xs text-ink/70 mt-2 italic">"{s.booking.message}"</p>}
                                            </div>
                                            {isResponsable && (
                                                <button onClick={() => removeSlot(s.id)} className="btn-outline !text-xs !px-3 !py-1.5 self-start"><Trash2 size={12} /> Annuler</button>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </main>
        </div>
    );
}

function SlotForm({ onClose, onSaved }) {
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [startTime, setStartTime] = useState("14:00");
    const [endTime, setEndTime] = useState("14:30");
    const [title, setTitle] = useState("Rendez-vous tuteur");
    const [saving, setSaving] = useState(false);

    const submit = async () => {
        if (!date || !startTime || !endTime) {
            toast.error("Date et horaires requis");
            return;
        }
        setSaving(true);
        try {
            await api.post("/agenda/slots", { date, start_time: startTime, end_time: endTime, title });
            toast.success("Créneau ajouté");
            onSaved();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
        } finally { setSaving(false); }
    };

    return (
        <div className="panel p-6 mb-8 border-2 border-orange/30" data-testid="slot-form">
            <div className="flex items-center justify-between mb-4">
                <h2 className="h-display text-xl font-semibold">Nouveau créneau</h2>
                <button onClick={onClose} className="text-muted2 hover:text-ink p-1"><X size={16} /></button>
            </div>
            <div className="grid sm:grid-cols-4 gap-3">
                <div>
                    <label className="label-eyebrow mb-2 block">Date</label>
                    <input type="date" className="field-input" value={date} onChange={(e) => setDate(e.target.value)} data-testid="slot-date-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Début</label>
                    <input type="time" className="field-input" value={startTime} onChange={(e) => setStartTime(e.target.value)} data-testid="slot-start-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Fin</label>
                    <input type="time" className="field-input" value={endTime} onChange={(e) => setEndTime(e.target.value)} data-testid="slot-end-input" />
                </div>
                <div>
                    <label className="label-eyebrow mb-2 block">Intitulé</label>
                    <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="slot-title-input" />
                </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
                <button onClick={onClose} className="btn-outline">Annuler</button>
                <button onClick={submit} disabled={saving} className="btn-primary" data-testid="slot-save-button">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {saving ? "Enregistrement…" : "Ajouter"}
                </button>
            </div>
        </div>
    );
}
